import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import { supabase } from "../supabaseClient";
import { FiSend, FiImage, FiX, FiSearch, FiUsers, FiCheckSquare, FiSquare, FiMessageSquare } from "react-icons/fi";
import { Button, Card, CardBody, PageHeader, EmptyState, Badge } from "./ui";

export default function Broadcast() {
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [mode, setMode] = useState("all");
  const [tokoList, setTokoList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingToko, setLoadingToko] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (mode === "selected" && tokoList.length === 0) {
      fetchToko();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview("");
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const fetchToko = async () => {
    setLoadingToko(true);
    const { data, error } = await supabase
      .from("toko")
      .select("user_id, kode_toko, email")
      .order("created_at", { ascending: false });

    if (error) {
      setStatus(`❌ Gagal memuat daftar toko: ${error.message}`);
      setTokoList([]);
    } else {
      setTokoList(data || []);
    }
    setLoadingToko(false);
  };

  const toggleSelect = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const filteredToko = tokoList.filter((t) => {
    const keyword = search.toLowerCase();
    return (
      String(t.kode_toko || "").toLowerCase().includes(keyword) ||
      String(t.email || "").toLowerCase().includes(keyword) ||
      String(t.user_id || "").toLowerCase().includes(keyword)
    );
  });

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("Ukuran gambar terlalu besar. Maksimal 10MB.");
      e.target.value = "";
      return;
    }
    setImageFile(file);
  };

  const handleBroadcast = async () => {
    if (!message.trim() && !imageFile) {
      alert("Pesan atau gambar tidak boleh kosong.");
      return;
    }

    if (mode === "selected" && selectedIds.length === 0) {
      alert("Pilih minimal satu toko tujuan.");
      return;
    }

    setSending(true);
    setStatus(null);

    const formData = new FormData();
    formData.append("message", message);
    if (imageFile) formData.append("photo", imageFile);
    if (mode === "selected") formData.append("user_ids", JSON.stringify(selectedIds));

    try {
      const res = await fetch(`${API_BASE_URL}/broadcast`, {
        method: "POST",
        body: formData,
      });

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }

      if (res.ok) {
        setStatus(`✅ Total berhasil kirim: ${data.total || 0} toko/user.`);
        setMessage("");
        setImageFile(null);
        setSelectedIds([]);
      } else if (res.status === 413) {
        setStatus("❌ Gagal mengirim: Ukuran gambar terlalu besar (maksimal 25MB).");
      } else {
        setStatus(`❌ Gagal mengirim: ${data.error || `Server error (${res.status})`}`);
      }
    } catch (error) {
      setStatus(`❌ Gagal mengirim: ${error.message}`);
    }

    setSending(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Broadcast"
        subtitle="Kirim pesan ke toko atau user terdaftar"
        icon={FiSend}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Left: recipients */}
        <Card className="lg:col-span-2">
          <CardBody className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Penerima</h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Pilih tujuan broadcast</p>
            </div>

            <div className="space-y-2">
              {[
                { value: "all", label: "Semua toko/user terdaftar", desc: "Kirim ke seluruh penerima" },
                { value: "selected", label: "Pilih beberapa toko/user", desc: "Tentukan penerima manual" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                    mode === opt.value
                      ? "border-primary-300 bg-primary-50/60 dark:border-primary-500/30 dark:bg-primary-500/10"
                      : "border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-800 dark:text-slate-200">{opt.label}</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">{opt.desc}</span>
                  </span>
                </label>
              ))}
            </div>

            {mode === "selected" && (
              <div className="space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="relative">
                  <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari kode toko, email, user ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="field-base pl-10"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    <Badge tone="primary">{selectedIds.length} dipilih</Badge>
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedIds(Array.from(new Set([...selectedIds, ...filteredToko.map((t) => t.user_id)])))}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                    >
                      Pilih semua
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIds([])}
                      className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
                    >
                      Kosongkan
                    </button>
                  </div>
                </div>

                {loadingToko ? (
                  <p className="py-6 text-center text-sm text-slate-400">Memuat daftar toko...</p>
                ) : filteredToko.length === 0 ? (
                  <EmptyState icon={FiUsers} title="Tidak ada toko" description="Tidak ada penerima yang cocok." className="py-8" />
                ) : (
                  <div className="max-h-80 space-y-1 overflow-y-auto scrollbar-slim">
                    {filteredToko.map((toko) => {
                      const checked = selectedIds.includes(toko.user_id);
                      return (
                        <button
                          key={toko.user_id}
                          type="button"
                          onClick={() => toggleSelect(toko.user_id)}
                          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                            checked ? "bg-primary-50/60 dark:bg-primary-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
                          }`}
                        >
                          {checked ? (
                            <FiCheckSquare className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <FiSquare className="h-4 w-4 flex-shrink-0 text-slate-300 dark:text-slate-600" />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-200">{toko.kode_toko || "-"}</span>
                            <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                              {toko.email || "-"} · ID: {toko.user_id}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Right: composer */}
        <Card className="lg:col-span-3">
          <CardBody className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Message Composer</h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Tulis pesan dan lampirkan gambar bila perlu</p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Pesan / Caption</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tulis pesan broadcast..."
                rows={8}
                className="field-base resize-y"
              />
              <p className="mt-1.5 text-right text-xs text-slate-400">{message.length} karakter</p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Gambar (opsional)</label>
              {imagePreview ? (
                <div className="relative inline-block">
                  <img src={imagePreview} alt="Preview" className="max-h-40 rounded-xl border border-slate-200 object-cover dark:border-slate-700" />
                  <button
                    type="button"
                    onClick={() => setImageFile(null)}
                    className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-danger-500 text-white shadow-md transition-colors hover:bg-danger-600"
                    aria-label="Hapus gambar"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 px-6 py-8 text-center transition-colors hover:border-primary-300 hover:bg-primary-50/40 dark:border-slate-700 dark:hover:border-primary-500/40 dark:hover:bg-primary-500/5">
                  <FiImage className="mb-2 h-6 w-6 text-slate-400" />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Pilih gambar</span>
                  <span className="mt-0.5 text-xs text-slate-400">PNG, JPG maks. 10MB</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
              )}
            </div>
          </CardBody>

          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row">
            <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <FiUsers className="h-4 w-4" />
              Penerima:{" "}
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                {mode === "all" ? "Semua toko" : `${selectedIds.length} toko`}
              </span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setMessage("");
                  setImageFile(null);
                  setSelectedIds([]);
                  setStatus(null);
                }}
              >
                Cancel
              </Button>
              <Button icon={FiSend} loading={sending} onClick={handleBroadcast}>
                {sending ? "Mengirim..." : "Kirim Broadcast"}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {status && (
        <Card className={`p-4 text-sm ${status.startsWith("✅") ? "border-success-100 bg-success-50 text-success-700 dark:border-success-500/20 dark:bg-success-500/10 dark:text-success-300" : "border-danger-100 bg-danger-50 text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300"}`}>
          <div className="flex items-center gap-2">
            <FiMessageSquare className="h-4 w-4" />
            {status}
          </div>
        </Card>
      )}
    </div>
  );
}
